import { css, FlattenSimpleInterpolation } from 'styled-components';

/**
 * PLA-8844: the scrollbar recipe for the AI screens' INNER scroll areas — the View more overlay
 * body, the Growth Planner slide-out, the event timeline, the Customer 360 breakdown body and the
 * Orbit panel. The page-level scrollbar is deliberately untouched: nothing here selects `html`,
 * `body` or the app layout scroller, so the recipe only ever reaches a container that opts in.
 */

// Also the track `scrollbar-gutter: stable` reserves outside the padding, for a container that has to
// keep a drawn inset on its scrolling side.
export const INTELLIGENCE_SCROLLBAR_SIZE = 8;
const SCROLLBAR_SIZE = `${INTELLIGENCE_SCROLLBAR_SIZE}px`;
// The thumb is drawn inside a transparent border so it reads as a pill floating in the track rather
// than a bar butted against the content edge. `background-clip: padding-box` is what keeps the
// background off that border; without it the border paints the thumb colour and the inset is lost.
const THUMB_INSET = '2px';
const THUMB_RADIUS = '999px';

/* The colours carry a literal fallback, which the rest of the Intelligence theme does not: the
   `--is-*` custom properties are declared only on `IntelligenceThemeScope`, and the Orbit panel
   mounts outside every Intelligence route (containers/Orbit/styles.ts). Bare `var(--is-border-strong)`
   would make the whole declaration invalid there and hand Orbit the OS scrollbar back. The fallbacks
   are the token values themselves, --is-text-muted and --is-text-secondary in theme/tokens.ts.

   The resting thumb is the MUTED INK rather than --is-border-strong: at 8px wide less the 2px inset
   the thumb paints 4px, and #2A405F over the #101830 panel was invisible in the Chromium capture
   (browser-recheck47). A scrollbar nobody can see is the reported complaint, not the fix. */
const THUMB_COLOR = 'var(--is-text-muted, #8592AD)';
const THUMB_HOVER_COLOR = 'var(--is-text-secondary, #AEBBD0)';

// The space the user asked for between the content and the bar, --is-space-12 with the same
// out-of-scope fallback as the colours.
const GUTTER = 'var(--is-space-12, 12px)';

export type TIntelligenceScrollAxis = 'x' | 'y' | 'both';

export interface IIntelligenceScrollbarOptions {
  /**
   * Adds the gutter padding to the scrolling side. Pass it only on a container with no padding of
   * its own: `scrollbar-gutter` reserves the track BETWEEN the padding box and the border, so a
   * container that already pads its content is separated from the bar by that padding, and a
   * `padding-right` here would overwrite the shorthand it set rather than add to it.
   */
  pad?: boolean;
}

/* Firefox and Chromium both ship the standard `scrollbar-width` / `scrollbar-color`, but Chromium
   IGNORES every `::-webkit-scrollbar-*` rule on an element whose `scrollbar-width` is not `auto`.
   Declaring both unconditionally would therefore cost Chrome, Edge and Safari-16.4+ the 8px pill and
   leave them on the narrower engine-drawn bar. The standard pair is gated behind the absence of the
   pseudo-element instead, which splits the engines exactly: Firefox does not implement
   `::-webkit-scrollbar`, so the query is true and it takes the standard pair; Chromium and
   Safari 16.4+ do, so it is false and they take the webkit block; Safari below 16.4 has no
   `selector()` support at all, so the unknown query is false there too and it also takes the webkit
   block. Firefox below 64 matches nothing and keeps its default bar. */
const engineRules = css`
  @supports not selector(::-webkit-scrollbar) {
    scrollbar-width: thin;
    scrollbar-color: ${THUMB_COLOR} transparent;
  }

  &::-webkit-scrollbar {
    width: ${SCROLLBAR_SIZE};
    height: ${SCROLLBAR_SIZE};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border: ${THUMB_INSET} solid transparent;
    border-radius: ${THUMB_RADIUS};
    background: ${THUMB_COLOR};
    background-clip: padding-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${THUMB_HOVER_COLOR};
    background-clip: padding-box;
  }
`;

/* `scrollbar-gutter: stable` reserves the track's width whether or not the bar is showing, so the
   content does not reflow the moment it appears. Only the block-end/inline-end gutter exists, which
   is why an x-only container gets padding and no gutter. */
const gutterFor = (axis: TIntelligenceScrollAxis, pad: boolean): FlattenSimpleInterpolation => css`
  ${axis !== 'x' &&
  css`
    scrollbar-gutter: stable;
  `}
  ${pad &&
  axis !== 'x' &&
  css`
    padding-right: ${GUTTER};
  `}
  ${pad &&
  axis !== 'y' &&
  css`
    padding-bottom: ${GUTTER};
  `}
`;

/**
 * The one scrollbar mixin for the AI screens. `axis` says which way the container scrolls — `y`,
 * `x`, or `both`.
 */
export const intelligenceScrollbar = (
  axis: TIntelligenceScrollAxis = 'y',
  { pad = false }: IIntelligenceScrollbarOptions = {}
): FlattenSimpleInterpolation => css`
  ${engineRules}
  ${gutterFor(axis, pad)}
`;
